<script setup lang="ts">
/**
 * Affiche la file de messages éphémères en haut de l'écran, sous l'encoche. Chaque message se
 * ferme seul, d'un balayage vers le haut, ou avec la croix.
 */
import {
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastRoot,
  ToastTitle,
  ToastViewport,
} from 'reka-ui'

import { fr } from '@/copy/fr'

import type { IconName } from '../icons/registry'
import { dismissToast, toastDuration, toasts } from '../toast'
import Icon from './Icon.vue'

const ICONS: Record<'success' | 'info' | 'error', IconName> = {
  success: 'check',
  info: 'info',
  error: 'alert',
}
</script>

<template>
  <ToastProvider swipe-direction="up" :label="fr.ui.notifications">
    <ToastRoot
      v-for="toast in toasts"
      :key="toast.id"
      :duration="toastDuration(toast.tone)"
      :type="toast.tone === 'error' ? 'foreground' : 'background'"
      class="flex items-start gap-3 rounded-control bg-ink py-3 pr-1.5 pl-4 text-ink-inverse transition-opacity duration-160 data-[state=closed]:opacity-0 data-[swipe=move]:translate-y-(--reka-toast-swipe-move-y)"
      @update:open="(open) => !open && dismissToast(toast.id)"
    >
      <Icon
        :name="ICONS[toast.tone]"
        :class="toast.tone === 'error' ? 'text-danger-on-ink' : 'text-ink-inverse'"
      />
      <div class="flex flex-1 flex-col gap-0.5">
        <ToastTitle class="text-label">{{ toast.title }}</ToastTitle>
        <ToastDescription v-if="toast.description" class="text-caption opacity-80">
          {{ toast.description }}
        </ToastDescription>
      </div>
      <ToastClose
        class="-my-1.5 flex size-9 shrink-0 items-center justify-center rounded-full text-ink-inverse/80 transition-colors duration-80 active:bg-ink-muted"
        :aria-label="fr.ui.close"
      >
        <Icon name="close" :size="16" />
      </ToastClose>
    </ToastRoot>
    <ToastViewport
      class="fixed inset-x-0 top-3 z-60 flex flex-col gap-2 px-gutter pt-safe outline-none"
    />
  </ToastProvider>
</template>
